// Iter 55.1b — universal API error surface.
//
// Every error from the backend flows through here. The goal is that
// no user ever sees "Request failed with status code 500" again —
// they always get a "what happened + what to do next" pair.
//
// USAGE:
//   import { apiErrorToast, formatApiError } from "@/lib/apiErrors";
//   catch (e) { apiErrorToast(e, toast); }
//
// Prefers, in order:
//   1. Backend envelope: { detail: { error, message, next_step } }
//   2. Backend simple:   { detail: "string" }
//   3. Pydantic array:   { detail: [{msg, loc, ...}] }
//   4. Network / offline: err.message + inferred next_step
//   5. Bare axios fallback

import { KNOWN_ERROR_COPY } from "./errorCopy";

const SUPPORT_MAIL = "mailto:support@bookmyinterview.ai";

// ---------------------------------------------------------------------
// normalize(err) — the single source of truth
// ---------------------------------------------------------------------
export function normalizeApiError(err) {
    // Case 1 — axios threw before any response arrived (offline, DNS,
    // Cloudflare 5xx that killed the connection, etc.). No `err.response`.
    if (err && !err.response) {
        const msg = (err.message || "").toLowerCase();
        if (msg.includes("network") || msg.includes("fetch")) {
            return {
                title: "Connection problem",
                message: "We couldn't reach the server just now.",
                next_step: "Check your internet and try again in a moment.",
                severity: "network",
                code: "NETWORK_ERROR",
            };
        }
        if (msg.includes("timeout") || err.code === "ECONNABORTED") {
            return {
                title: "This took too long",
                message: "The server didn't respond in time.",
                next_step: "Please try again — if it keeps happening, refresh the page.",
                severity: "network",
                code: "TIMEOUT",
            };
        }
        return {
            title: "Something went wrong",
            message: err.message || "Unexpected error.",
            next_step: "Please refresh and try again.",
            severity: "unknown",
            code: "UNKNOWN",
        };
    }

    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;

    // Case 2 — structured envelope (Iter 55.1b standard).
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
        const code = typeof detail.error === "string" ? detail.error : null;
        const fromLib = code && KNOWN_ERROR_COPY[code];
        return {
            title: fromLib?.title || detail.title || defaultTitleForStatus(status),
            message: detail.message || fromLib?.message || "Something went wrong.",
            next_step: detail.next_step || fromLib?.next_step || defaultNextStepForStatus(status),
            severity: severityForStatus(status),
            code: code || `HTTP_${status}`,
        };
    }

    // Case 3 — pydantic validation array.
    if (Array.isArray(detail)) {
        const first = detail[0];
        const msg = first && typeof first.msg === "string" ? first.msg : "One or more fields are invalid.";
        const rawLoc = first && Array.isArray(first.loc) ? first.loc.filter(x => typeof x === "string") : [];
        const fieldLoc = rawLoc.filter(x => x !== "body" && x !== "payload" && x !== "query" && x !== "path" && x !== "header" && x !== "cookie");
        // If the validation error's loc is empty OR only contains meta
        // segments (body / payload / query / path / header / cookie) that
        // don't map to a visible form field, the copy "Fix the highlighted
        // field" is a lie — nothing on screen is highlighted. That
        // condition is the tell for a REAL body-shape mismatch between the
        // frontend and the endpoint's request model (see the P1 dead
        // scorecard submit we shipped a fix for). Say so honestly.
        if (fieldLoc.length === 0) {
            return {
                title: "Something went wrong on our side",
                message: "The request didn't match what the server expected. Our team can see this — please try again.",
                next_step: "Try again in a moment. If it keeps failing, contact support with the code below.",
                severity: "error",
                code: "BODY_SHAPE_MISMATCH",
            };
        }
        return {
            title: "Check your form",
            message: `${fieldLoc.join(" · ")}: ${msg}`,
            next_step: "Fix the highlighted field and submit again.",
            severity: "validation",
            code: "VALIDATION_ERROR",
        };
    }

    // Case 4 — string detail. Try to enrich by status code.
    if (typeof detail === "string" && detail.trim()) {
        return {
            title: defaultTitleForStatus(status),
            message: detail,
            next_step: defaultNextStepForStatus(status),
            severity: severityForStatus(status),
            code: `HTTP_${status}`,
        };
    }

    // Case 5 — final fallback.
    return {
        title: defaultTitleForStatus(status),
        message: err?.message || "Unexpected error.",
        next_step: defaultNextStepForStatus(status),
        severity: severityForStatus(status),
        code: `HTTP_${status || "UNKNOWN"}`,
    };
}

function defaultTitleForStatus(status) {
    if (!status) return "Something went wrong";
    if (status === 401) return "Sign in required";
    if (status === 403) return "Not allowed";
    if (status === 404) return "Not found";
    if (status === 409) return "Conflict";
    if (status === 413) return "Too large";
    if (status === 422) return "Check your form";
    if (status === 429) return "Slow down a moment";
    if (status >= 500) return "Something went wrong on our side";
    return "Something went wrong";
}

function defaultNextStepForStatus(status) {
    if (!status) return "Please try again.";
    if (status === 401) return "Log in and try again.";
    if (status === 403) return "If you should have access, ask your account admin.";
    if (status === 404) return "Double-check the link — if you followed it from us, please contact support.";
    if (status === 409) return "Please review and try again.";
    if (status === 413) return "Use a smaller file and try again.";
    if (status === 422) return "Fix the highlighted field and submit again.";
    if (status === 429) return "Wait a moment and try again.";
    if (status >= 500) return "Please try again in a moment. If it keeps happening, contact support.";
    return "Please try again.";
}

function severityForStatus(status) {
    if (!status) return "unknown";
    if (status >= 500) return "server";
    if (status === 429) return "rate-limit";
    if (status >= 400) return "client";
    return "unknown";
}

// ---------------------------------------------------------------------
// formatApiError(err) — legacy string API, still supported. Returns the
// "message · next_step" line for callers that only render one string.
// ---------------------------------------------------------------------
export function formatApiError(err) {
    const n = normalizeApiError(err);
    if (n.next_step && n.next_step !== n.message) {
        return `${n.message} ${n.next_step}`;
    }
    return n.message;
}

// ---------------------------------------------------------------------
// apiErrorToast(err, toast) — the recommended usage everywhere.
// ---------------------------------------------------------------------
export function apiErrorToast(err, toast) {
    if (!toast) return;
    const n = normalizeApiError(err);
    const isServerOrNetwork = n.severity === "server" || n.severity === "network";
    toast.error(n.title, {
        description: n.next_step
            ? `${n.message}\n\n${n.next_step}`
            : n.message,
        // Sonner supports `action` for a clickable button in the toast.
        action: isServerOrNetwork
            ? {
                label: "Contact support",
                onClick: () => { window.location.href = SUPPORT_MAIL; },
            }
            : undefined,
        duration: isServerOrNetwork ? 12000 : 6000,
    });
}
